import classNames from "classnames";
import { greaterThan, subtract } from "dinero.js";
import { Immutable } from "immer";
import React, { useState } from "react";
import SVG from "react-inlinesvg";
import { useStore } from "react-redux";

import iconUpsellModalSelectIcon from "../../../img/icons/upsell-modal-select-icon.svg";
import iconUpsellModalSelectedIcon from "../../../img/icons/upsell-modal-selected-icon.svg";
import { defaults } from "../../apps/configurator/defaults";
import { filteredLevelColumnsSelector } from "../../apps/configurator/selectors";
import { ConfiguratorTypes } from "../../constants";
import { useMountEffect } from "../../hooks/useMountEffect";
import {
    IOptionCode,
    IOptionSingleValue,
    IProduct,
    InternalProduct,
} from "../../models/catalogue";
import { IImageURL, IProductID } from "../../models/nominals";
import { useAppSelector } from "../../store/reducers";
import { IRootState } from "../../store/reducers.interfaces";
import {
    buildRelevantLevelVariants,
    findLevelVariants,
} from "../../store/selectors";
import { PriceType, getProductPrice } from "../../utils/catalogue";
import { format } from "../../utils/format";
import { usePageSetting } from "../../utils/settings";
import { Image } from "../Image";

import styles from "./index.module.scss";

export interface ILevelColumn {
    level: string;
    variant: InternalProduct | null;
}

interface ICoreFeature {
    icon: string | null;
    copy: string | null;
}

interface IVariantLevelCoreFeatures {
    variant: IProductID;
    core_features_list: ICoreFeature[];
}

const CoreFeaturesList = (props: {
    core_features: ICoreFeature[];
    isOneColumn: boolean;
}) => {
    const containerClasses = classNames({
        [styles.coreFeatures]: true,
        [styles.oneColumnCoreFeatures]: props.isOneColumn,
    });
    return (
        <div className={containerClasses}>
            <h3 className={styles.featuresHeading}>Core Features</h3>
            <ul className={styles.featuresList}>
                {props.core_features.map((feature, idx) => (
                    <li key={idx} className={styles.featuresItem}>
                        {feature.icon && (
                            <Image
                                className={styles.featuresIcon}
                                src={feature.icon}
                            />
                        )}
                        <span>{feature.copy}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const buildImage = (
    rootProduct: IProduct,
    product: InternalProduct,
): IImageURL => {
    // use variant images if they exist
    if (rootProduct.images.length > 0) {
        return rootProduct.images[0].original;
    }
    // else use the variant image
    return product.images[0].original;
};

const buildPrice = (
    product: InternalProduct,
    firstColumnVariant: InternalProduct | null,
    isOneColumn: boolean,
): string | null => {
    if (firstColumnVariant) {
        const firstColumnProductPrice = getProductPrice(
            firstColumnVariant.price,
            {
                priceType: PriceType.COSMETIC_EXCL_TAX,
                includePostDiscountAddons: true,
                quantity: 1,
            },
        );
        const columnProductPrice = getProductPrice(product.price, {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: 1,
        });

        if (isOneColumn) {
            return format.money(firstColumnProductPrice);
        }

        if (greaterThan(columnProductPrice, firstColumnProductPrice)) {
            return `+ ${format.money(
                subtract(columnProductPrice, firstColumnProductPrice),
            )}`;
        }
        return null;
    }
    return null;
};

const buildUI = (
    level: string,
    idx: number,
    isOneColumn: boolean,
    rootProductId: IProductID,
    selectorID: string,
    code: IOptionCode,
    value: IOptionSingleValue | undefined,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
): React.JSX.Element => {
    const optionNameSuffix = `${rootProductId as unknown as number}-${selectorID}-${code}-${idx}`;
    const optionName = `configurator__radio-option--${optionNameSuffix}`;
    const optionsGroupName = `${code}-${selectorID}`;
    const optionSelected = level === value;

    const actionText = idx === 0 ? "Select" : "Upgrade";
    const textLabel = optionSelected ? "Selected" : actionText;
    const icon = optionSelected ? (
        <SVG
            className={styles.selectedIcon}
            src={iconUpsellModalSelectedIcon}
        />
    ) : (
        <SVG className={styles.selectIcon} src={iconUpsellModalSelectIcon} />
    );
    const buttonClasses = classNames({
        [styles.selectButton]: true,
        [styles.selectedButton]: optionSelected,
        [styles.oneColumnButton]: isOneColumn,
        "al-level-selection-button": true,
    });

    return (
        <label className={buttonClasses}>
            <input
                type="radio"
                id={optionName}
                name={optionsGroupName}
                onChange={onChange}
                value={level}
                checked={optionSelected}
            />
            <div>
                {icon}
                {textLabel}
            </div>
        </label>
    );
};

const buildLevelColumns = (
    columns: ILevelColumn[],
    levelMarketingCopy: Record<
        string,
        { main_copy: string; description_copy: string }
    >,
    variantLevelCoreFeaturesData: IVariantLevelCoreFeatures[],
    rootProduct: IProduct,
    selectorID: string,
    code: IOptionCode,
    value: IOptionSingleValue | undefined,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
): React.JSX.Element[] => {
    return columns.map((column, idx) => {
        const thisLevelMarketingCopy = levelMarketingCopy[column.level];
        const thisLevelCoreFeatures = variantLevelCoreFeaturesData.filter(
            (data: IVariantLevelCoreFeatures) => {
                return data.variant === column.variant?.id;
            },
        );

        const columnClasses = classNames({
            [styles.column]: true,
            [styles.oneColumn]: columns.length === 1,
        });

        const mainCopyClasses = classNames({
            [styles.mainCopy]: true,
            [styles.oneColumnMainCopy]: columns.length === 1,
        });

        const imageClasses = classNames({
            [styles.image]: true,
            [styles.oneColumnImage]: columns.length === 1,
        });

        const descriptionCopyClasses = classNames({
            [styles.descriptionCopy]: true,
            [styles.oneColumnDescriptionCopy]: columns.length === 1,
        });

        const productNameClasses = classNames({
            [styles.productName]: true,
            [styles.oneColumnProductName]: columns.length === 1,
        });

        const priceClasses = classNames({
            [styles.price]: true,
            [styles.oneColumnPrice]: columns.length === 1,
        });

        const firstColumnVariant = columns[0].variant;

        return (
            <li key={column.level} className={columnClasses}>
                {thisLevelMarketingCopy && (
                    <span className={mainCopyClasses}>
                        {thisLevelMarketingCopy.main_copy}
                    </span>
                )}
                {column.variant && (
                    <Image
                        className={imageClasses}
                        src={buildImage(rootProduct, column.variant)}
                    />
                )}
                {thisLevelMarketingCopy && (
                    <span className={descriptionCopyClasses}>
                        {thisLevelMarketingCopy.description_copy}
                    </span>
                )}
                <h2 className={productNameClasses}>{column.variant?.title}</h2>
                <span className={priceClasses}>
                    {column.variant &&
                        buildPrice(
                            column.variant,
                            firstColumnVariant,
                            columns.length === 1,
                        )}
                </span>
                {buildUI(
                    column.level,
                    idx,
                    columns.length === 1,
                    rootProduct.id,
                    selectorID,
                    code,
                    value,
                    onChange,
                )}

                {thisLevelCoreFeatures.length > 0 && (
                    <CoreFeaturesList
                        core_features={
                            thisLevelCoreFeatures[0].core_features_list
                        }
                        isOneColumn={columns.length === 1}
                    />
                )}
            </li>
        );
    });
};

export const LevelSelectionPanel = (
    props: Immutable<{
        configuratorType?: ConfiguratorTypes;
        selectorID: string;
        rootProduct: IProduct;
        variant: IProduct | null;
        code: IOptionCode;
        value?: IOptionSingleValue;
        options: IOptionSingleValue[];
        prefixNameCapped: string;
        configuratorClass: string;
        prefixClass: string;
        onChange: (event: React.FormEvent<HTMLInputElement>) => void;
        isIcon?: boolean;
        showPricing?: boolean;
        isPlpPanelStyle?: boolean;
    }>,
): React.ReactNode => {
    const [columns, setColumns] = useState<ILevelColumn[]>([
        { level: "", variant: null },
    ]);

    const optionValues = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.optionValues;
    });

    const levelMarketingCopy = usePageSetting("level_marketing_copy") || {};
    const variantLevelCoreFeaturesRaw = usePageSetting(
        "variant_level_core_features",
    );
    const variantLevelCoreFeaturesData: IVariantLevelCoreFeatures[] =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        JSON.parse(
            typeof variantLevelCoreFeaturesRaw === "string"
                ? variantLevelCoreFeaturesRaw
                : "[]",
        );

    const store = useStore<IRootState>();

    useMountEffect(() => {
        const filteredLevelColumns = filteredLevelColumnsSelector(
            store.getState().configurator || defaults,
        );
        if (filteredLevelColumns) {
            setColumns(filteredLevelColumns);
            return;
        }
        const levelVariants = buildRelevantLevelVariants(
            [...props.options],
            optionValues,
            props.rootProduct,
            props.code,
        );
        // We can only show 2 variants, so if there are 3,
        const levelColumns =
            levelVariants.length > 2
                ? findLevelVariants(levelVariants, optionValues)
                : levelVariants;

        setColumns(levelColumns);
    });

    const containerClasses = classNames({
        [styles.grid]: true,
        [styles.oneColumnGrid]: columns.length === 1,
        "al-level-selection-panel": true,
    });

    return (
        <ul className={containerClasses}>
            {buildLevelColumns(
                columns,
                levelMarketingCopy,
                variantLevelCoreFeaturesData,
                props.rootProduct,
                props.selectorID,
                props.code,
                props.value,
                props.onChange,
            )}
        </ul>
    );
};
